import { qiDay } from '../validate';
import type { QiGroup } from '../manifest';

export type QiImportFormat = 'T' | 'U' | 'STATURA_CSV' | 'GPMS_ERRORS';
export interface QiImportRow {
  row_number: number;
  group_code: QiGroup | null;
  subgroup: string;
  identity: { aged_care_id?: string; name?: string; date_of_birth?: string; subject_id?: string };
  entry: Record<string, unknown>;
  observed_date: string | null;
  service_identifier?: string;
  reporting_period?: string;
  issues: string[];
  source_summary?: Record<string, unknown>;
}
export const QI_CSV_HEADERS = [
  'group_code',
  'aged_care_id',
  'name',
  'date_of_birth',
  'subject_id',
  'subgroup',
  'discipline',
  'mode',
  'observed_date',
  'criterion_json',
  'primary_exclusion_reason',
  'comment',
] as const;
export function qiImportHeaderTemplate(group: QiGroup): string {
  return QI_CSV_HEADERS.join(',') + '\n' + group + ','.repeat(QI_CSV_HEADERS.length - 1) + '\n';
}
export function parseQiImportDate(value: string, gpms = false): string {
  let result = value;
  if (gpms) {
    const match = /^([1-9]|[12]\d|3[01])\/(0[1-9]|1[012])\/(\d{4})$/.exec(value);
    if (!match) throw new Error('invalid_import_date');
    result = `${match[3]}-${match[2]}-${match[1]!.padStart(2, '0')}`;
  }
  if (!Number.isFinite(qiDay(result))) throw new Error('invalid_import_date');
  return result;
}
/** Quoted CSV, including embedded newlines; malformed/duplicate headers refuse the whole file. */
export function parseQiCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [],
    cell = '',
    quoted = false,
    closed = false;
  const input = text.replace(/^\uFEFF/, '');
  const finish = () => {
    row.push(cell);
    cell = '';
    closed = false;
  };
  for (let i = 0; i < input.length; i++) {
    const ch = input[i]!;
    if (quoted) {
      if (ch === '"' && input[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        quoted = false;
        closed = true;
      } else cell += ch;
    } else if (ch === '"') {
      if (cell || closed) throw new Error('invalid_import_csv');
      quoted = true;
    } else if (ch === ',') {
      finish();
    } else if (ch === '\r' || ch === '\n') {
      if (ch === '\r' && input[i + 1] === '\n') i++;
      finish();
      if (row.some(Boolean)) rows.push(row);
      row = [];
    } else {
      if (closed) throw new Error('invalid_import_csv');
      cell += ch;
    }
    if (cell.length > 16384 || row.length > 200 || rows.length > 5001)
      throw new Error('import_limit');
  }
  if (quoted) throw new Error('invalid_import_csv');
  if (cell || row.length) {
    finish();
    if (row.some(Boolean)) rows.push(row);
  }
  return rows;
}

export { default as QI_T_IMPORT_HEADERS } from './t-headers.json';
